import { asHash, asRecord, asString, type SignedEnvelope } from "./contracts.js";

const SIGNATURE_PREFIX = "ZKRESISTOR-SIGNED-ENVELOPE-V1\0";

export interface SignerPolicy {
  algorithm: "Ed25519";
  keyId: string;
  publicKeyBase64url: string;
}

function decodeBase64url(value: string, name: string, expectedBytes?: number): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value) || value.includes("=")) {
    throw new TypeError(`${name} must be canonical unpadded base64url`);
  }
  try {
    const padding = "=".repeat((4 - value.length % 4) % 4);
    const binary = atob(value.replace(/-/g, "+").replace(/_/g, "/") + padding);
    const decoded = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const canonical = btoa(String.fromCharCode(...decoded))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    if (canonical !== value || (expectedBytes !== undefined && decoded.byteLength !== expectedBytes)) {
      throw new Error("non-canonical encoding or invalid length");
    }
    return decoded;
  } catch (error) {
    throw new TypeError(`${name} must be canonical base64url`, { cause: error });
  }
}

export function parseSignedEnvelope(value: unknown): SignedEnvelope {
  const record = asRecord(value, "signed envelope");
  if (record.schemaVersion !== 1 || record.algorithm !== "Ed25519") {
    throw new Error("Unsupported signed envelope");
  }
  return {
    schemaVersion: 1,
    domain: asString(record.domain, "envelope domain"),
    algorithm: "Ed25519",
    keyId: asHash(record.keyId, "envelope keyId"),
    payloadSha256: asHash(record.payloadSha256, "envelope payloadSha256"),
    payloadBase64url: asString(record.payloadBase64url, "envelope payload"),
    signatureBase64url: asString(record.signatureBase64url, "envelope signature"),
  };
}

function toHex(bytes: Uint8Array): string {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function verifySignedEnvelope<T>(
  input: unknown,
  policy: SignerPolicy,
  expectedDomain: string,
  parsePayload: (value: unknown) => T,
): Promise<T> {
  const envelope = parseSignedEnvelope(input);
  if (
    envelope.keyId !== policy.keyId ||
    envelope.algorithm !== policy.algorithm ||
    envelope.domain !== expectedDomain
  ) {
    throw new Error("Gateway response was signed by an unpinned key");
  }
  const payload = decodeBase64url(envelope.payloadBase64url, "envelope payload");
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", payload.slice().buffer));
  if (toHex(digest) !== envelope.payloadSha256) throw new Error("Gateway response content hash mismatch");
  const domainPrefix = new TextEncoder().encode(`${SIGNATURE_PREFIX}${expectedDomain}\0`);
  const signatureInput = new Uint8Array(domainPrefix.byteLength + digest.byteLength);
  signatureInput.set(domainPrefix);
  signatureInput.set(digest, domainPrefix.byteLength);
  const signatureMessage = await crypto.subtle.digest("SHA-256", signatureInput.slice().buffer);
  const publicKey = await crypto.subtle.importKey(
    "raw",
    decodeBase64url(policy.publicKeyBase64url, "indexer public key", 32).slice().buffer,
    { name: "Ed25519" },
    false,
    ["verify"],
  );
  const valid = await crypto.subtle.verify(
    { name: "Ed25519" },
    publicKey,
    decodeBase64url(envelope.signatureBase64url, "envelope signature", 64).slice().buffer,
    signatureMessage,
  );
  if (!valid) throw new Error("Gateway response signature is invalid");
  let decoded: unknown;
  try {
    decoded = JSON.parse(new TextDecoder().decode(payload));
  } catch (error) {
    throw new TypeError("Gateway response payload is not JSON", { cause: error });
  }
  return parsePayload(decoded);
}
